import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Row,
  Col,
  Icon,
  Button,
  FormControl,
  Form,
  SelectPicker,
  IconButton,
  Checkbox
} from 'rsuite';
import {
  every,
  get,
  getOr,
  has,
  isEmpty,
  isEqual,
  keyBy,
  map,
  reduce,
  reject,
  set,
  size
} from 'lodash/fp';
import { useSelector } from 'react-redux';
import {
  getColumnOptions,
  getFilterFieldsSettings,
  VISIT_ON_FIELD
} from '../../../constants/visit/AssocVisitFields';
import { toFieldOptions } from '../../guest/Form/settings';
import fieldPropsByType from '../../../helpers/fieldPropsByType';
import { VISIT_HISTORY } from '../../../helpers/ReportTemplateTypes';
import { toObjectDateRange } from '../../../helpers/dates';
import { hasViewAccess } from '../../../helpers/permission';
import * as PERMISSIONS from '../../../constants/permissions/guest';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import CollapsiblePresets from '../../CollapsiblePresets';
import ReportTemplateFormModal from '../../../containers/ReportTemplateFormModal';
import {
  ActionGridCol,
  StyledFormGroup,
  StyledControlLabel,
  IncludeAssocCheckbox,
  ColumnsTagPicker
} from './elements';
import { AddressFieldsWithPermissionMap } from '../../../constants/GuestAddressFieldsWithPermission';

const toFilterOptions = (filter, filterFields) => {
  const existingFieldsFilter = keyBy('field', filter);
  return reduce((options, { name: value, label, group }) => {
    if (has(value, existingFieldsFilter)) return options;
    return [...options, { value, label, group }];
  }, [])(filterFields);
};

const filterToFormValue = reduce(
  (result, { field, value }) => ({ ...result, [field]: value }),
  {}
);

const getTemplateFile = ({ file }, hasAddressPermission) => {
  if (hasAddressPermission) return file;

  const columns = reject(each => {
    const [, field] = each.split('.');
    return has(field, AddressFieldsWithPermissionMap);
  }, file.columns);

  const filter = reject(each => {
    const [, field] = each.field.split('.');
    return has(field, AddressFieldsWithPermissionMap);
  }, file.filter);

  return { ...file, columns, filter };
};

const VisitHistorySearchForm = ({
  query,
  onChange,
  onSubmit,
  onReset,
  isFetching
}) => {
  const formRef = useRef();
  const { columns, filter, includeAssocVisits } = query;
  const permissions = useSelector(getUserPermissionsAsMap);
  const hasViewAddressAccess = hasViewAccess(
    PERMISSIONS.VIEW_ADDRESS,
    permissions
  );
  const filterFields = getFilterFieldsSettings(hasViewAddressAccess);
  const defaultFormValue = useMemo(() => filterToFormValue(filter), []);
  const columnFilterOptions = useMemo(
    () => toFilterOptions(filter, filterFields),
    [filter, filterFields]
  );
  const columnOptions = useMemo(() => getColumnOptions(hasViewAddressAccess), [
    hasViewAddressAccess
  ]);
  const [formValue, setFormValue] = useState(defaultFormValue);
  const [presetData, setPresetData] = useState({});
  const [showPresetFormModal, setShowPresetFormModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [checkAllColumns, setCheckallColumns] = useState();
  const [indeterminateColumns, setIndeterminateColumns] = useState(false);

  useEffect(
    () => {
      if (isEmpty(columns)) {
        setCheckallColumns(false);
        setIndeterminateColumns(false);
        return;
      }
      const isAllSelected = size(columnOptions) === size(columns);
      if (isAllSelected) {
        setCheckallColumns(true);
        setIndeterminateColumns(false);
      } else {
        setIndeterminateColumns(true);
      }
    },
    [columns, columnOptions]
  );

  const disabledSubmit = useMemo(
    () => {
      const filterValues = map('value', filter);
      const isEmptyQuery = every(val => !val || isEmpty(val), filterValues);
      const isEmptyColumns = isEmpty(columns);

      return isEmptyQuery || isEmptyColumns;
    },
    [filter, columns]
  );

  const handleCheckAllColumns = (_, checked) => {
    const allColumns = checked ? map('value', columnOptions) : [];
    onChange(set('columns', allColumns, query));
  };

  const handleSelectColumns = newColumns => {
    onChange(set('columns', newColumns, query));
  };

  const handleChangeFilter = value => {
    setFormValue(value);
    const newFilter = map(
      each => ({ ...each, value: get(each.field, value) }),
      filter
    );
    onChange(set('filter', newFilter, query));
  };

  const handleChangeFilterField = field => value => {
    setFormValue({ ...formValue, [field]: value });
  };

  const handleAddFilter = field => {
    const newFilter = [...filter, { field, value: '', isStatic: 0 }];
    onChange(set('filter', newFilter, query));
  };

  const handleRemoveFilter = field => () => {
    const newFilter = reject(each => isEqual(field, each.field), filter);
    onChange(set('filter', newFilter, query));
  };

  const handleSubmit = () => {
    onSubmit();
  };

  const handleOnReset = () => {
    onReset();
    setFormValue({});
    setSelectedPreset(null);
    formRef.current = null;
  };

  const handleSelectTemplate = template => {
    const file = getTemplateFile(template, hasViewAddressAccess);
    const filterFormValue = reduce(
      (result, { field, value }) => ({
        ...result,
        [field]: field === VISIT_ON_FIELD ? toObjectDateRange(value) : value
      }),
      {}
    )(file.filter);

    onChange(file);
    setSelectedPreset(template);
    setFormValue(filterFormValue);
    setShowPresetFormModal(false);
  };

  const handleShowPresetFormModal = () => {
    setPresetData({ columns, filter, includeAssocVisits });
    setShowPresetFormModal(true);
  };

  const handleHidePresetFormModal = () => {
    setShowPresetFormModal(false);
  };

  const handleOnSavedPreset = () => {
    handleHidePresetFormModal();
  };

  const toggleIncludeAssocVisits = () => {
    onChange({ ...query, includeAssocVisits: includeAssocVisits ? 0 : 1 });
  };

  return (
    <Grid fluid style={{ marginLeft: 0 }}>
      <Row gutter={32}>
        <Col xs={24}>
          <CollapsiblePresets
            type={VISIT_HISTORY}
            headerLabel={
              selectedPreset
                ? `Preset: "${get('fileName', selectedPreset)}"`
                : 'Select Preset'
            }
            onSelect={handleSelectTemplate}
          />
          <ReportTemplateFormModal
            file={presetData}
            fileType={VISIT_HISTORY}
            show={showPresetFormModal}
            onSubmitted={handleOnSavedPreset}
            handleClose={handleHidePresetFormModal}
          />
        </Col>
        <Col md={24} lg={8} style={{ paddingBottom: 16 }}>
          <StyledControlLabel>Filter Parameters *</StyledControlLabel>
          <Form
            ref={formRef}
            layout="horizontal"
            formValue={formValue}
            onChange={handleChangeFilter}
          >
            {map(({ field, isStatic }) => {
              const fieldSetting = get(field, filterFields);
              const label = get('label', fieldSetting);
              const options = get('options', fieldSetting);
              const fieldType = get('fieldType', fieldSetting);
              const fieldProps = getOr(() => ({}), fieldType, fieldPropsByType)(
                {
                  style: { width: '100%' },
                  options: toFieldOptions(options),
                  onChange: handleChangeFilterField(field)
                }
              );

              return (
                <StyledFormGroup key={`field-${field}`}>
                  <FormControl
                    name={field}
                    placeholder={label}
                    {...fieldProps}
                  />
                  {!isStatic && (
                    <IconButton
                      color="red"
                      appearance="link"
                      onClick={handleRemoveFilter(field)}
                      icon={<Icon icon="close-circle" />}
                      style={{ position: 'absolute', right: -15 }}
                    />
                  )}
                </StyledFormGroup>
              );
            }, filter)}
          </Form>
          <div>
            <SelectPicker
              style={{ width: '100%', marginRight: 10 }}
              value={columns}
              data={columnFilterOptions}
              placeholder="Select Column Filter"
              onChange={handleAddFilter}
              disabled={isFetching}
            />
          </div>
        </Col>
        <Col md={24} lg={8} style={{ paddingBottom: 16 }}>
          <StyledControlLabel style={{ paddingTop: 8, paddingBottom: 8 }}>
            Choose Columns to Export
          </StyledControlLabel>
          <ColumnsTagPicker
            cleanable={false}
            value={columns}
            data={columnOptions}
            groupBy="group"
            placeholder="Select Columns"
            onChange={handleSelectColumns}
            disabled={isFetching}
            renderExtraFooter={() => (
              <Checkbox
                inline
                onChange={handleCheckAllColumns}
                indeterminate={indeterminateColumns}
                checked={checkAllColumns}
              >
                Check all
              </Checkbox>
            )}
          />
          <IncludeAssocCheckbox
            checked={!!includeAssocVisits}
            onChange={toggleIncludeAssocVisits}
            style={{ display: 'none' }}
          >
            Include Association Visits
          </IncludeAssocCheckbox>
        </Col>
        <ActionGridCol md={24} lg={8}>
          <Button onClick={handleOnReset} disabled={isFetching}>
            <Icon icon="refresh" /> Reset
          </Button>
          <Button
            color="green"
            appearance="primary"
            onClick={handleSubmit}
            disabled={isFetching || disabledSubmit}
          >
            <Icon icon="search" /> Search
          </Button>
          <Button
            appearance="ghost"
            onClick={handleShowPresetFormModal}
            disabled={isFetching || isEmpty(columns)}
          >
            <Icon icon="save" /> Save Preset
          </Button>
        </ActionGridCol>
      </Row>
    </Grid>
  );
};

VisitHistorySearchForm.propTypes = {
  query: PropTypes.shape({
    filter: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.array
        ]).isRequired,
        isStatic: PropTypes.number.isRequired
      })
    ).isRequired,
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    includeAssocVisits: PropTypes.number.isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired
};

export default VisitHistorySearchForm;
